/**
 * Admin Routes Configuration
 *
 * Defines all routes for admin user pages.
 */

import type { ReactElement } from 'react';
import { BarChart3, Users, ClipboardList, Award, CreditCard, MessageSquare, BookOpen, Settings, Search } from 'lucide-react';

// Admin Pages
import { AdminDashboardPage } from '../pages/admin/AdminDashboardPage';
import { UserManagementPage } from '../pages/admin/UserManagementPage';
import { UserDetailPage } from '../pages/admin/UserDetailPage';
import { ApplicationListPage as AdminApplicationListPage } from '../pages/admin/ApplicationListPage';
import { AuditOversightPage } from '../pages/admin/AuditOversightPage';
import { SystemSettingsPage } from '../pages/admin/SystemSettingsPage';
import { CertificateListPage as AdminCertificateListPage } from '../pages/admin/CertificateListPage';
import { CertificateIssuancePage } from '../pages/admin/CertificateIssuancePage';
import { TrainingManagementPage } from '../pages/admin/TrainingManagementPage';
import { PaymentRecordsPage } from '../pages/admin/PaymentRecordsPage';
import { SupportDashboardPage } from '../pages/admin/SupportDashboardPage';
import { TicketQueuePage } from '../pages/admin/TicketQueuePage';
import { AdminTicketDetailPage } from '../pages/admin/AdminTicketDetailPage';
import { KnowledgeBaseManagementPage } from '../pages/admin/KnowledgeBaseManagementPage';

/**
 * Route definition interface
 */
export interface RouteDefinition {
  path: string;
  element: ReactElement;
  label: string;
  icon?: ReactElement;
  showInNav?: boolean;
  children?: RouteDefinition[];
}

/**
 * Admin routes configuration
 */
export const adminRoutes: RouteDefinition[] = [
  {
    path: '/admin/dashboard',
    element: <AdminDashboardPage />,
    label: 'Dashboard',
    icon: <BarChart3 className="w-5 h-5" />,
    showInNav: true,
  },
  {
    path: '/admin/users',
    element: <UserManagementPage />,
    label: 'User Management',
    icon: <Users className="w-5 h-5" />,
    showInNav: true,
    children: [
      {
        path: '/admin/users/:id',
        element: <UserDetailPage />,
        label: 'User Details',
        showInNav: false,
      },
    ],
  },
  {
    path: '/admin/applications',
    element: <AdminApplicationListPage />,
    label: 'Applications',
    icon: <ClipboardList className="w-5 h-5" />,
    showInNav: true,
    children: [
      {
        path: '/admin/applications/:id',
        element: <AdminApplicationListPage />,
        label: 'Application Details',
        showInNav: false,
      },
    ],
  },
  {
    path: '/admin/audits',
    element: <AuditOversightPage />,
    label: 'Audit Oversight',
    icon: <Search className="w-5 h-5" />,
    showInNav: true,
    children: [
      {
        path: '/admin/audits/:id',
        element: <AuditOversightPage />,
        label: 'Audit Details',
        showInNav: false,
      },
    ],
  },
  {
    path: '/admin/certificates',
    element: <AdminCertificateListPage />,
    label: 'Certificates',
    icon: <Award className="w-5 h-5" />,
    showInNav: true,
    children: [
      {
        path: '/admin/certificates/issue',
        element: <CertificateIssuancePage />,
        label: 'Issue Certificate',
        showInNav: false,
      },
    ],
  },
  {
    path: '/admin/training',
    element: <TrainingManagementPage />,
    label: 'Training',
    icon: <BookOpen className="w-5 h-5" />,
    showInNav: true,
  },
  {
    path: '/admin/payments',
    element: <PaymentRecordsPage />,
    label: 'Payments',
    icon: <CreditCard className="w-5 h-5" />,
    showInNav: true,
  },
  {
    path: '/admin/support',
    element: <SupportDashboardPage />,
    label: 'Support',
    icon: <MessageSquare className="w-5 h-5" />,
    showInNav: true,
    children: [
      {
        path: '/admin/support/tickets',
        element: <TicketQueuePage />,
        label: 'Ticket Queue',
        showInNav: false,
      },
      {
        path: '/admin/support/tickets/:ticketId',
        element: <AdminTicketDetailPage />,
        label: 'Ticket Details',
        showInNav: false,
      },
      {
        path: '/admin/support/knowledge-base',
        element: <KnowledgeBaseManagementPage />,
        label: 'Knowledge Base',
        showInNav: false,
      },
    ],
  },
  {
    path: '/admin/settings',
    element: <SystemSettingsPage />,
    label: 'System Settings',
    icon: <Settings className="w-5 h-5" />,
    showInNav: true,
  },
];

/**
 * Get all routes (flattened)
 */
export function getAllAdminRoutes(): RouteDefinition[] {
  const routes: RouteDefinition[] = [];

  function flattenRoutes(routeList: RouteDefinition[]) {
    routeList.forEach((route) => {
      routes.push(route);
      if (route.children) {
        flattenRoutes(route.children);
      }
    });
  }

  flattenRoutes(adminRoutes);
  return routes;
}

/**
 * Get navigation items (routes shown in sidebar)
 */
export function getAdminNavigationItems(): RouteDefinition[] {
  return adminRoutes.filter((route) => route.showInNav);
}

/**
 * Get route by path
 */
export function getAdminRouteByPath(path: string): RouteDefinition | undefined {
  const allRoutes = getAllAdminRoutes();
  return allRoutes.find((route) => route.path === path);
}

export default adminRoutes;